import React, { useState } from "react";
import { Formik, Form, FormikProps } from "formik";
import Typography from "../../../designComponents/Typography";
import UploadFile from "../../../designComponents/UploadFile";
import { uploadFilesToS3OnlyKeys } from "../../../utils/s3Upload";
import Button from "../../../designComponents/Button";
import { PropertyAddressType } from "./types";
import { AddPropertyProps } from ".";
import { useParams } from "react-router-dom";

interface PhotoFormValues extends PropertyAddressType {
  images: (string | File)[];
}

const Photo: React.FC<AddPropertyProps> = ({
  initialValues,
  onUpdate,
  gotoPrevious,
  loading,
  setLoading,
}) => {
  const [existingPhotoUrls, setExistingPhotoUrls] = useState<string[]>(
    initialValues?.images?.filter(
      (image): image is string => typeof image === "string"
    ) || []
  );
  const { propertyId } = useParams<{ propertyId: string }>();

  return (
    <div>
      <Typography style={{ marginBottom: "10px" }} size="h3">
        Step 4: Property Photos
      </Typography>
      <Formik<PhotoFormValues>
        initialValues={{
          ...initialValues,
          images: existingPhotoUrls,
        }}
        enableReinitialize
        onSubmit={async (values) => {
          let uploadedPhotoUrls: string[] = [];
          setLoading(true);
          try {
            if (values.images && values.images.length > 0) {
              const filesToUpload = values.images.filter(
                (photo): photo is File => photo instanceof File
              );
              const uploadedFiles = await uploadFilesToS3OnlyKeys(
                {
                  category:'property',fileUri:filesToUpload,propertyId:propertyId??''
                }
              );
              if (uploadedFiles && uploadedFiles.length > 0) {
                uploadedPhotoUrls = uploadedFiles;
              }
            }

            const allPhotos = [
              ...existingPhotoUrls,
              ...uploadedPhotoUrls,
              ...values.images
                .filter((photo): photo is File => photo instanceof File)
                .map((file) => URL.createObjectURL(file)),
            ];

            const dataToSubmit = {
              ...values,
              images: allPhotos,
            };

            setLoading(false);
            onUpdate(dataToSubmit);
          } catch (error) {
            console.error("Error uploading files:", error);
            setLoading(false);
          }
        }}
      >
        {(formikProps: FormikProps<PhotoFormValues>) => {
          const handleFileChange = (files: File[] | File | null) => {
            if (files) {
              const fileArray = Array.isArray(files) ? files : [files];
              const newPhotos = [...formikProps.values.images, ...fileArray];
              formikProps.setFieldValue("images", newPhotos);
              setExistingPhotoUrls(
                newPhotos.filter(
                  (photo): photo is string => typeof photo === "string"
                )
              );
            }
          };

          const handleRemoveFile = (removedFileUrl: string) => {
            setExistingPhotoUrls(
              existingPhotoUrls.filter((url) => url !== removedFileUrl)
            );

            formikProps.setFieldValue(
              "images",
              formikProps.values.images.filter((photo) =>
                typeof photo === "string" ? photo !== removedFileUrl : true
              )
            );
          };

          return (
            <Form>
              <Typography style={{ marginBottom: "10px" }} size="h4">
                You can upload multiple images at once
              </Typography>
              <UploadFile
                title="Upload Photos"
                name="propertyPhotos"
                onChange={(files) => handleFileChange(files)}
                variant="previewImage"
                apiUrls={existingPhotoUrls}
                onRemove={(index) =>
                  handleRemoveFile(existingPhotoUrls[index!])
                }
                enableMultipleFile={true}
              />
              <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
                <Button
                  type="button"
                  radius="normal"
                  onClick={gotoPrevious}
                  isDisabled={loading}
                >
                  Previous
                </Button>
                <Button type="submit" radius="normal" isLoading={loading}>
                  Next
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default Photo;
